import iconArrow from '@/shared/icons/arrow/arrow.vue';

import hadrianLabel from '../hadrianLabel/hadrianLabel.vue';

enum Keys {
  Enter = 'Enter',
  Escape = 'Escape',
  Tab = 'Tab',
  Down = 'ArrowDown',
  Up = 'ArrowUp',
}

import { defineComponent } from 'vue';

export default defineComponent({
  name: 'hadrianSelect',
  props: {
    inputLabel: {
      type: String,
    },
    mandatory: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    variant: {
      type: String,
      default: '',
    },
    value: {},
    options: {
      type: Array as () => any[],
      required: true,
    },
    getOptionKey: {
      type: Function as never as () => (option: any) => string | number,
    },
    getOptionLabel: {
      type: Function as never as () => (option: any) => string | number,
    },
    avoidScrolling: {
      type: Boolean,
    },
  },
  components: {
    iconArrow,
    hadrianLabel,
  },
  data() {
    return {
      hidden: true,
      internalValue: this.value,
      hasBeenOpened: false,
    };
  },
  computed: {
    inputButton(): HTMLButtonElement {
      return this.$refs.inputButton as HTMLButtonElement;
    },
    expandableItemList(): HTMLUListElement {
      return this.$refs.expandableItemList as HTMLUListElement;
    },
    listElements(): HTMLLIElement[] {
      return Array.from(this.expandableItemList.childNodes) as HTMLLIElement[];
    },
    nextItem(): any | undefined {
      if (!this.internalValue || !this.options.includes(this.internalValue)) {
        return this.options[0];
      }

      return this.options.find((_, index) => this.isOptionSelected(this.options[index - 1]));
    },
    previousItem(): any | undefined {
      if (!this.internalValue || !this.options.includes(this.internalValue)) {
        return this.options[0];
      }

      return this.options.find((_, index) => this.isOptionSelected(this.options[index + 1]));
    },
    uniqueId(): string {
      return Date.now().toString();
    },
    buttonId(): string {
      return `select-button-${this.uniqueId}`;
    },
    labelId(): string {
      return `select-label-${this.uniqueId}`;
    },
    hasNullOption(): boolean {
      return this.options.some(option => option == null);
    },
  },
  methods: {
    getMatchingElement(item: any): HTMLLIElement | undefined {
      return this.listElements.find(element => element.id === this.getId(item));
    },
    show() {
      this.hidden = false;
      this.hasBeenOpened = true;
      this.expandableItemList.classList.remove('hidden');
      this.expandableItemList.focus();

      if (this.internalValue) {
        this.scrollToElement(this.internalValue);
      }
    },
    hide() {
      this.hidden = true;
      this.emitValue();
      this.expandableItemList.classList.add('hidden');
    },
    toggleHidden() {
      if (this.hidden) {
        this.show();
      } else {
        this.hide();
      }
    },
    hardSelect(option: any) {
      this.internalValue = option;
      this.hide();
    },
    softSelect(option: any) {
      this.internalValue = option;
      this.scrollToElement(option);
    },
    emitValue() {
      this.$emit('input', this.internalValue);
    },
    onButtonKeyPress(event: KeyboardEvent) {
      switch (event.key) {
        case Keys.Down:
        case Keys.Up:
        case Keys.Enter:
          event.preventDefault();
          this.show();
          break;
        default:
          if (this.searchOptions(event.key)) {
            this.hardSelect(this.searchOptions(event.key));
          }
          break;
      }
    },
    onListKeyPress(event: KeyboardEvent) {
      switch (event.key) {
        case Keys.Escape:
        case Keys.Enter:
          event.preventDefault();
          this.hide();
          this.inputButton.focus();
          break;
        case Keys.Tab:
          this.hide();
          break;
        case Keys.Down:
          event.preventDefault();
          if (this.nextItem) {
            this.softSelect(this.nextItem);
          }
          break;
        case Keys.Up:
          event.preventDefault();
          if (this.previousItem) {
            this.softSelect(this.previousItem);
          }
          break;
        default:
          if (this.searchOptions(event.key)) {
            this.softSelect(this.searchOptions(event.key));
          }
          break;
      }
    },
    characterMatchesOption(character: string, option: any): boolean {
      const label = this.getLabel(option)?.toLowerCase() || '';
      return label.startsWith(character.toLowerCase());
    },
    searchOptions(character: string): any | undefined {
      const currentValueIndex = this.internalValue
        ? this.options.findIndex(option => option === this.internalValue)
        : -1;
      if (currentValueIndex === -1) {
        return this.getFirstElementMatching(character);
      }

      const goingDown = character.toLowerCase() === character;
      const matchingOptions = this.options.filter(
        (option, index) =>
          this.characterMatchesOption(character, option) &&
          (goingDown ? index > currentValueIndex : index < currentValueIndex)
      );

      if (matchingOptions.length === 0) {
        return this.getFirstElementMatching(character);
      }

      return goingDown ? matchingOptions[0] : matchingOptions[matchingOptions.length - 1];
    },
    getFirstElementMatching(character: string): any | undefined {
      return this.options.find(option => this.characterMatchesOption(character, option));
    },
    scrollToElement(item: any) {
      const element = this.getMatchingElement(item);
      if (!element) {
        return;
      }

      if (!this.isElementVisible(element)) {
        this.expandableItemList.scrollTop = element.offsetTop;
      }
    },
    isOptionSelected(option: any) {
      return this.internalValue === option;
    },
    isElementVisible(element: HTMLLIElement): boolean {
      const containerTop = this.expandableItemList.scrollTop;
      const containerBottom = containerTop + this.expandableItemList.clientHeight;

      const elementTop = element.offsetTop;
      const elementBottom = elementTop + element.clientHeight;

      return elementTop >= containerTop && elementBottom <= containerBottom;
    },
    getLabel(option: any): string {
      if (!option && !this.hasNullOption) {
        return '';
      }

      if (this.getOptionLabel) {
        return this.getOptionLabel(option).toString();
      }

      return option.toString();
    },
    getKey(option: any): string {
      if (!option && !this.hasNullOption) {
        return '';
      }

      if (this.getOptionKey) {
        return this.getOptionKey(option).toString();
      }

      return option.toString();
    },
    getId(option: any): string {
      return `${this.getKey(option)}-${this.uniqueId}}`;
    },
  },
  watch: {
    options() {
      if (!this.options.includes(this.internalValue) && !this.disabled) {
        this.hardSelect(this.options[0]);
      }
    },
    value() {
      this.internalValue = this.value;
    },
  },
});
